import { useEffect, useState } from 'react';

interface UseSearchDebounceOptions {
  value?: string;
  onChange?: (value: string) => void;
  timeout: number;
}

/**
 * Manages a local input value that fires `onChange` only after `timeout` ms
 * of inactivity. Shared between `SearchFieldWithDebounce` and `Toolbar`.
 */
export function useSearchDebounce({ value, onChange, timeout }: UseSearchDebounceOptions): {
  localValue: string | undefined;
  setLocalValue: (v: string) => void;
} {
  const [localValue, setLocalValue] = useState(value);

  // Keep local state in sync when the parent resets the value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Fire onChange after the debounce delay
  useEffect(() => {
    const id = setTimeout(() => {
      if (localValue !== value) onChange?.(localValue ?? '');
    }, timeout);
    return () => {
      clearTimeout(id);
    };
  }, [localValue]);

  return { localValue, setLocalValue };
}
